import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Typography from '@lib/components/Typography/Typography';
import WarningYellowIcon from '@lib/assets/icons/extended_warning_yellow_48.svg?react';
import IconButton from '@lib/components/IconButton/IconButton';
import { TechnicalPause } from '@lib/hooks/useTechnicalPause';
import { getFormattedDate, transformDate } from '@lib/utils/dateTimeHelpers';
import { dateTimeFormatEU } from '@lib/enums/dateTime';
import { useLocalStorage } from 'usehooks-ts';
import { TECHNICAL_PAUSE_BANNER } from '@lib/enums/localStorageKeys';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import styles from './TechnicalPauseBanner.module.scss';

type Props = {
  upcomingTechnicalPause: TechnicalPause | null | undefined;
};

interface BannerState extends TechnicalPause {
  closedAt?: string;
}

export default function TechnicalPauseBanner({
  upcomingTechnicalPause,
}: Props) {
  const { t } = useTranslation();
  const [state, setState] = useLocalStorage<BannerState | null>(
    TECHNICAL_PAUSE_BANNER,
    null,
  );

  useEffect(() => {
    if (upcomingTechnicalPause?.id && upcomingTechnicalPause.id !== state?.id) {
      setState({
        ...upcomingTechnicalPause,
        closedAt: undefined,
      });
    }
  }, [setState, state?.id, upcomingTechnicalPause]);

  if (!upcomingTechnicalPause || state?.closedAt) return null;

  const { startAt, endAt } = upcomingTechnicalPause;

  const onClose = () => {
    setState((prevState) =>
      prevState?.id
        ? { ...prevState, closedAt: transformDate(new Date()) }
        : prevState,
    );
  };

  return (
    <PageBody paddingTop="small" paddingBottom="none">
      <div className={styles.root}>
        <span className={styles.icon}>
          <WarningYellowIcon />
        </span>
        <div className={styles.content}>
          <div className={styles.text}>
            {/* Convert to ISO string so getFormattedDate applies CET timezone
                conversion. Passing Date objects directly skips toZonedTime,
                causing incorrect display when browser timezone differs from CET. */}
            <Typography variant="body" strong>
              {getFormattedDate(startAt.toISOString(), dateTimeFormatEU)} -{' '}
              {getFormattedDate(endAt.toISOString(), dateTimeFormatEU)}
            </Typography>{' '}
            <Typography variant="body">{t('maintenance-text')}</Typography>
          </div>
          <Typography variant="caption" className={styles.desc}>
            {t('maintenance-desc')}
          </Typography>
        </div>
        <span className={styles.button}>
          <IconButton icon="close" onClick={onClose} />
        </span>
      </div>
    </PageBody>
  );
}
